'use client';

import { ProductCard } from './ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import type { Product, KortPassform } from '@/types';
import { useEffect, useMemo, useState } from 'react';
import { useCartStore } from '@/stores/cart';
import { api } from '@/lib/api';
import { distinguishingFeatures, kategoriNyckel } from '@/lib/distinguishing-features';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface ProductGridProps {
  products: Product[];
  loading?: boolean;
}

export function ProductGrid({ products, loading }: ProductGridProps) {
  const { locale } = useLocale();

  /**
   * Vilka attribut SKILJER de visade produkterna åt?
   *
   * Kortet hade tidigare en fast lista över vad som fick visas, och `Spänning
   * [V]` stod på den. En sökning efter generator till en VW Touran gav därför
   * fem träffar som alla hette "Generator", alla från JP Group, alla märkta
   * "14V" — det enda attribut som var identiskt för samtliga. Det avgörande,
   * laddströmmen (110, 140 och 150 A), fanns i datan men visades aldrig.
   * Varken en verkstad eller en privatperson kunde välja.
   *
   * Nu räknas det ut per resultatsida: attribut vars värde varierar mellan
   * korten lyfts fram, det som är lika för alla utelämnas. Regeln behöver ingen
   * underhållen lista per kategori — bromsskivor får diameter, generatorer
   * ampere, filter höjd, av sig själva.
   */
  // Regeln bor i lib/distinguishing-features.ts — samma i butiken och i
  // B2B-portalen, så en produkt visar samma särskiljande specar överallt.
  const highlightPerKategori = useMemo(() => distinguishingFeatures(products), [products]);

  /**
   * Passform ur TecDoc för hela sidan på en gång, när kunden valt bil (ägaren 2026-09-28:
   * "rätt produkter till våra bilar och matchningar"). Kortet får "Passar exakt",
   * "Passar på villkor" eller "Passar inte", och på sidan läggs exakt passande först och
   * de som inte passar sist — inom sidan, så sidnumreringen inte rubbas.
   */
  const ktype = useCartStore((s) => s.selectedVehicle?.ktype);
  const plate = useCartStore((s) => s.selectedPlate);
  const [passform, setPassform] = useState<Record<number, KortPassform>>({});
  const idNyckel = products.map((p) => p.id).join(',');
  useEffect(() => {
    setPassform({});
    if (!ktype || !idNyckel) return;
    let aktuell = true;
    api.passformLista(idNyckel.split(',').map(Number), ktype, plate)
      .then((r) => { if (aktuell) setPassform(r.passform ?? {}); })
      .catch(() => {});
    return () => { aktuell = false; };
  }, [idNyckel, ktype, plate]);

  const ordning = useMemo(() => {
    const vikt = (id: number) => ({ passar: 0, villkor: 1, passar_ej: 3 } as Record<string, number>)[passform[id]?.status ?? ''] ?? 2;
    return products.map((p, i) => ({ p, i })).sort((a, b) => vikt(a.p.id) - vikt(b.p.id) || a.i - b.i).map((x) => x.p);
  }, [products, passform]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="aspect-square w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t(locale, 'product.noProductsFound')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {ordning.map((product, i) => (
        <ProductCard
          key={product.id}
          product={product}
          passform={passform[product.id]}
          priority={i < 8}
          highlightFeatures={highlightPerKategori.get(kategoriNyckel(product)) ?? []}
        />
      ))}
    </div>
  );
}
